import React from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Search as SearchIcon, SlidersHorizontal, X, Save, Tag, Download, ListPlus, Loader2, Bookmark, Trash2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { api, downloadSelectionCsv } from "@/lib/api";
import { LeadsTable } from "@/components/LeadsTable";
import { TableSkeleton } from "@/components/TableSkeleton";
import { LeadDetailDrawer } from "@/components/LeadDetailDrawer";
import { useT } from "@/i18n/LanguageContext";

const emptyFilters = { job_titles: [], seniority: [], locations: [], company_size: [], sub_industry: [], keywords: [], result_count: 12 };

const FilterSidebar = ({ filters, setFilters, options, onClear, t, savedSearches, onLoadSearch, onDeleteSearch }) => {
  const toggleArr = (key, val) => {
    setFilters((f) => {
      const arr = f[key] || [];
      return { ...f, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });
  };
  return (
    <div className="space-y-5">
      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("seniority")}</div>
        <ToggleGroup type="multiple" value={filters.seniority} className="flex flex-wrap justify-start gap-1.5">
          {(options.seniority || []).map((s) => (
            <ToggleGroupItem key={s} value={s} size="sm" variant="outline" className="h-7 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" onClick={() => toggleArr("seniority", s)} data-testid={`filter-seniority-${s}`}>
              {s}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      <Separator />
      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("company_size")}</div>
        <div className="flex flex-wrap gap-1.5">
          {(options.company_size || []).map((c) => (
            <button key={c} onClick={() => toggleArr("company_size", c)} data-testid={`filter-size-${c}`}
              className={`rounded-md border px-2 py-1 text-xs transition-colors ${(filters.company_size || []).includes(c) ? "border-primary bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>
              {c}
            </button>
          ))}
        </div>
      </div>
      <Separator />
      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("sub_industry")}</div>
        <div className="flex flex-col gap-2 max-h-56 overflow-y-auto pr-1">
          {(options.sub_industry || []).map((s) => (
            <label key={s} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={(filters.sub_industry || []).includes(s)} onCheckedChange={() => toggleArr("sub_industry", s)} data-testid={`filter-subindustry-${s}`} />
              {s}
            </label>
          ))}
        </div>
      </div>
      <Separator />
      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("locations")}</div>
        <Input placeholder={t("locations_placeholder")} value={(filters.locations || []).join(", ")}
          onChange={(e) => setFilters((f) => ({ ...f, locations: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) }))}
          data-testid="filter-locations-input" />
      </div>
      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("titles_keywords")}</div>
        <Input placeholder={t("titles_placeholder")} value={(filters.job_titles || []).join(", ")}
          onChange={(e) => setFilters((f) => ({ ...f, job_titles: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) }))}
          data-testid="filter-titles-input" />
      </div>
      <div>
        <Label className="text-xs">{t("num_results")}: {filters.result_count}</Label>
        <input type="range" min="4" max="25" value={filters.result_count} className="mt-2 w-full accent-[hsl(var(--primary))]"
          onChange={(e) => setFilters((f) => ({ ...f, result_count: Number(e.target.value) }))} data-testid="filter-result-count" />
      </div>
      <Button variant="ghost" className="w-full" onClick={onClear} data-testid="filters-clear-all-button">{t("clear_all_filters")}</Button>

      <Separator />
      <div>
        <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Bookmark className="h-3.5 w-3.5" /> {t("saved_searches")}</div>
        {(!savedSearches || savedSearches.length === 0) ? (
          <p className="text-xs text-muted-foreground">{t("no_saved_searches")}</p>
        ) : (
          <div className="space-y-1.5">
            {savedSearches.map((ss) => (
              <div key={ss.id} data-testid="saved-search-item" className="flex items-center justify-between rounded-lg border bg-card px-2.5 py-1.5">
                <button className="flex min-w-0 items-center gap-2 text-left" onClick={() => onLoadSearch(ss)} data-testid="saved-search-load">
                  <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate text-xs font-medium">{ss.name}</span>
                </button>
                <button onClick={() => onDeleteSearch(ss.id)} aria-label="Delete" data-testid="saved-search-delete" className="text-muted-foreground hover:text-destructive">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default function SearchPage() {
  const location = useLocation();
  const t = useT();
  const [query, setQuery] = React.useState(location.state?.query || "");
  const [filters, setFilters] = React.useState(emptyFilters);
  const [options, setOptions] = React.useState({});
  const [leads, setLeads] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [searched, setSearched] = React.useState(false);
  const [selected, setSelected] = React.useState([]);
  const [lists, setLists] = React.useState([]);
  const [detail, setDetail] = React.useState(null);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [newListName, setNewListName] = React.useState("");
  const [bulkTag, setBulkTag] = React.useState("");
  const [savedSearches, setSavedSearches] = React.useState([]);
  const [saveOpen, setSaveOpen] = React.useState(false);
  const [saveName, setSaveName] = React.useState("");
  const [history, setHistory] = React.useState([]);
  const [viewMode, setViewMode] = React.useState("history");

  const loadSearches = () => api.getSearches().then((d) => setSavedSearches(d.searches || [])).catch(() => {});
  const loadHistory = () => api.getLeads({ limit: 500 }).then((d) => setHistory(d.leads || [])).catch(() => {});

  React.useEffect(() => {
    api.options().then(setOptions).catch(() => {});
    api.getLists().then((d) => setLists(d.lists || [])).catch(() => {});
    loadSearches();
    loadHistory();
  }, []);

  const activeChips = React.useMemo(() => {
    const chips = [];
    ["seniority", "company_size", "sub_industry", "locations", "job_titles"].forEach((k) => {
      (filters[k] || []).forEach((v) => chips.push({ k, v }));
    });
    return chips;
  }, [filters]);

  const removeChip = (k, v) => setFilters((f) => ({ ...f, [k]: (f[k] || []).filter((x) => x !== v) }));

  const runSearch = async (overrideFilters) => {
    const f = overrideFilters || filters;
    const hasChips = ["seniority", "company_size", "sub_industry", "locations", "job_titles"].some((k) => (f[k] || []).length);
    if (!query.trim() && !hasChips) {
      toast.error(t("enter_desc_or_filters"));
      return;
    }
    setLoading(true);
    setSearched(true);
    setViewMode("results");
    setSelected([]);
    try {
      const payload = { query: query.trim() || null, filters: f };
      const data = await api.searchLeads(payload);
      setLeads(data.leads || []);
      if (data.filters) setFilters((prev) => ({ ...prev, ...data.filters }));
      loadHistory();
      if (!data.leads || data.leads.length === 0) toast.info(t("no_leads_generated"));
      else {
        const dup = (data.leads || []).filter((l) => l.already_existed).length;
        toast.success(t("found_leads", { n: data.leads.length }) + (dup ? ` · ${t("dup_in_results", { n: dup })}` : ""));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("search_failed"));
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected((s) => (s.length === leads.length ? [] : leads.map((l) => l.id)));

  const openDetail = (lead) => { setDetail(lead); setDetailOpen(true); };
  const onLeadChanged = (updated) => {
    setLeads((ls) => ls.map((l) => (l.id === updated.id ? updated : l)));
    setDetail(updated);
  };

  const doBulkSave = async () => {
    await api.bulkSave(selected);
    setLeads((ls) => ls.map((l) => (selected.includes(l.id) ? { ...l, saved: true } : l)));
    toast.success(t("saved_n_leads", { n: selected.length }));
  };
  const doBulkTag = async () => {
    if (!bulkTag.trim()) return;
    await api.bulkTag(selected, [bulkTag.trim()]);
    toast.success(t("tagged_n_leads", { n: selected.length }));
    setBulkTag("");
  };
  const doExport = async () => { await downloadSelectionCsv(selected); toast.success(t("csv_exported")); };

  const addToList = async (listId, ids) => {
    await api.addToList(listId, ids);
    const l = lists.find((x) => x.id === listId);
    toast.success(t("added_to", { name: l?.name || "" }));
    api.getLists().then((d) => setLists(d.lists || []));
  };

  const createListAndAdd = async () => {
    if (!newListName.trim()) return;
    const created = await api.createList(newListName.trim());
    await api.addToList(created.id, selected);
    toast.success(t("added_to", { name: created.name }));
    setNewListName("");
    api.getLists().then((d) => setLists(d.lists || []));
  };

  const saveSearch = async () => {
    if (!saveName.trim()) return;
    await api.createSearch({ name: saveName.trim(), query: query, filters });
    toast.success(t("search_saved"));
    setSaveName("");
    setSaveOpen(false);
    loadSearches();
  };

  const loadSearch = (ss) => {
    setQuery(ss.query || "");
    const f = { ...emptyFilters, ...(ss.filters || {}) };
    setFilters(f);
    toast.success(ss.name);
    runSearch(f);
  };

  const deleteSearch = async (id) => {
    await api.deleteSearch(id);
    toast.success(t("search_deleted"));
    loadSearches();
  };

  return (
    <div>
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl border bg-card p-6 shadow-sm">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(900px_circle_at_15%_0%,hsla(196,78%,34%,0.12),transparent_55%),radial-gradient(700px_circle_at_95%_10%,hsla(38,92%,55%,0.10),transparent_50%)]" />
        <div className="relative">
          <div className="mb-1 flex items-center gap-2 text-sm font-medium text-primary"><Sparkles className="h-4 w-4" /> {t("ai_lead_search")}</div>
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">{t("search_title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("search_subtitle")}</p>

          <div className="mt-4 rounded-xl border bg-card shadow-sm">
            <Textarea
              data-testid="ai-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("search_placeholder")}
              className="min-h-[64px] resize-none border-0 focus-visible:ring-0"
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) runSearch(); }}
            />
            <div className="flex items-center justify-between gap-2 border-t px-3 py-2">
              <span className="hidden text-xs text-muted-foreground sm:block">{t("search_tip")}</span>
              <div className="flex gap-2">
                {query && <Button variant="ghost" size="sm" data-testid="ai-search-clear-button" onClick={() => setQuery("")}>{t("clear")}</Button>}
                <Button variant="outline" size="sm" onClick={() => setSaveOpen(true)} data-testid="save-search-button"><Bookmark className="mr-1.5 h-4 w-4" /> {t("save_search")}</Button>
                <Button size="sm" onClick={() => runSearch()} disabled={loading} data-testid="ai-search-run-button">
                  {loading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <SearchIcon className="mr-1.5 h-4 w-4" />}
                  {loading ? t("searching") : t("search_leads")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="mt-6 flex gap-6">
        <aside className="hidden w-[300px] shrink-0 lg:block">
          <div className="sticky top-6 rounded-xl border bg-card p-4 shadow-sm">
            <div className="mb-3 flex items-center gap-2 font-medium"><SlidersHorizontal className="h-4 w-4" /> {t("filters")}</div>
            <FilterSidebar filters={filters} setFilters={setFilters} options={options} onClear={() => setFilters(emptyFilters)} t={t} savedSearches={savedSearches} onLoadSearch={loadSearch} onDeleteSearch={deleteSearch} />
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="lg:hidden" data-testid="filters-open-button"><SlidersHorizontal className="mr-1.5 h-4 w-4" /> {t("filters")}</Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[320px] overflow-y-auto">
                <SheetHeader><SheetTitle>{t("filters")}</SheetTitle></SheetHeader>
                <div className="mt-4"><FilterSidebar filters={filters} setFilters={setFilters} options={options} onClear={() => setFilters(emptyFilters)} t={t} savedSearches={savedSearches} onLoadSearch={loadSearch} onDeleteSearch={deleteSearch} /></div>
              </SheetContent>
            </Sheet>
            {activeChips.map(({ k, v }) => (
              <Badge key={`${k}-${v}`} variant="secondary" className="gap-1">
                {v}
                <button onClick={() => removeChip(k, v)} aria-label={`Remove ${v}`}><X className="h-3 w-3" /></button>
              </Badge>
            ))}
            {activeChips.length > 0 && <button className="text-xs text-muted-foreground underline" onClick={() => setFilters(emptyFilters)}>{t("clear")}</button>}
          </div>

          <Tabs value={viewMode} onValueChange={setViewMode} className="mb-3">
            <TabsList>
              <TabsTrigger value="results" data-testid="tab-results">{t("tab_results")}{leads.length ? ` (${leads.length})` : ""}</TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history">{t("tab_history")}{history.length ? ` (${history.length})` : ""}</TabsTrigger>
            </TabsList>
          </Tabs>

          {selected.length > 0 && (
            <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
              data-testid="leads-bulk-actions-bar"
              className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border bg-card p-2 shadow-sm">
              <span className="px-2 text-sm font-medium">{t("selected", { n: selected.length })}</span>
              <Button variant="outline" size="sm" onClick={doBulkSave} data-testid="bulk-save-button"><Save className="mr-1.5 h-4 w-4" /> {t("save")}</Button>
              <div className="flex items-center gap-1">
                <Input value={bulkTag} onChange={(e) => setBulkTag(e.target.value)} placeholder={t("tag").toLowerCase()} className="h-8 w-24" data-testid="bulk-tag-input" />
                <Button variant="outline" size="sm" onClick={doBulkTag}><Tag className="mr-1.5 h-4 w-4" /> {t("tag")}</Button>
              </div>
              <Button variant="outline" size="sm" onClick={doExport} data-testid="bulk-export-button"><Download className="mr-1.5 h-4 w-4" /> {t("export_csv")}</Button>
              <Select onValueChange={(v) => addToList(v, selected)}>
                <SelectTrigger className="h-8 w-[150px]" data-testid="bulk-add-to-list"><SelectValue placeholder={t("add_to_list")} /></SelectTrigger>
                <SelectContent>{lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
              <div className="flex items-center gap-1">
                <Input value={newListName} onChange={(e) => setNewListName(e.target.value)} placeholder={t("new_list_name")} className="h-8 w-32" data-testid="bulk-new-list-input" />
                <Button variant="outline" size="sm" onClick={createListAndAdd}><ListPlus className="mr-1.5 h-4 w-4" /> {t("create")}</Button>
              </div>
            </motion.div>
          )}

          {viewMode === "results" ? (
            <>
              {loading && <><Progress value={65} className="mb-3 h-1" /><TableSkeleton rows={filters.result_count > 12 ? 10 : 8} /></>}

              {!loading && searched && leads.length === 0 && (
                <Card className="flex flex-col items-center justify-center gap-3 p-12 text-center">
                  <SearchIcon className="h-8 w-8 text-muted-foreground" />
                  <div className="font-medium">{t("no_results_title")}</div>
                  <p className="max-w-sm text-sm text-muted-foreground">{t("no_results_sub")}</p>
                  <Button variant="outline" onClick={() => setFilters(emptyFilters)}>{t("clear_all_filters")}</Button>
                </Card>
              )}

              {!loading && !searched && (
                <Card className="flex flex-col items-center justify-center gap-3 p-12 text-center">
                  <Sparkles className="h-8 w-8 text-primary" />
                  <div className="font-medium">{t("start_first_search")}</div>
                  <p className="max-w-md text-sm text-muted-foreground">{t("start_first_search_sub")}</p>
                </Card>
              )}

              {!loading && leads.length > 0 && (
                <LeadsTable leads={leads} selected={selected} onToggle={toggle} onToggleAll={toggleAll} onOpen={openDetail} />
              )}
            </>
          ) : (
            <>
              {history.length === 0 ? (
                <Card className="flex flex-col items-center justify-center gap-3 p-12 text-center">
                  <Clock className="h-8 w-8 text-muted-foreground" />
                  <div className="font-medium">{t("already_searched")}</div>
                  <p className="max-w-sm text-sm text-muted-foreground">{t("history_empty")}</p>
                </Card>
              ) : (
                <>
                  <p className="mb-2 text-xs text-muted-foreground">{t("history_hint")}</p>
                  <LeadsTable leads={history} selected={selected} onToggle={toggle} onToggleAll={toggleAll} onOpen={openDetail} />
                </>
              )}
            </>
          )}
        </div>
      </div>

      <LeadDetailDrawer
        lead={detail}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        statusOptions={options.status}
        lists={lists}
        onChanged={onLeadChanged}
        onAddToList={addToList}
      />

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("save_search_title")}</DialogTitle></DialogHeader>
          <div>
            <Label>{t("search_name")}</Label>
            <Input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder={t("search_name_placeholder")} className="mt-1" data-testid="save-search-name-input" onKeyDown={(e) => e.key === "Enter" && saveSearch()} />
          </div>
          <DialogFooter><Button onClick={saveSearch} data-testid="save-search-submit">{t("save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
